import React, { useEffect, useState, useContext } from "react";
import "./QrCodeInstall.css"; // Custom CSS for the page
import QrCode from "./QrCode/QrCode";
import appConfig from "../../constants/config";
import { MasterContainerContext } from "../Layout/Layout";
import { detectBrowser } from "../../services/browserDetection";

const MAX_RETRIES = 15; // Maximum number of retries
const RETRY_INTERVAL = 2000; // Interval between retries (in milliseconds)

const QrCodeInstall: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isInstallPromptVisible, setInstallPromptVisible] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const { os } = useContext(MasterContainerContext) || {};

    // Retry mechanism with a delay
    const retryInstallPrompt = () => {
        if (retryCount < MAX_RETRIES) {
            setTimeout(() => {
                if (deferredPrompt) {
                    setInstallPromptVisible(true); // Show the button if the prompt is available
                    console.log("DeferredPrompt found, showing install button.");
                } else {
                    console.log(`Retrying... Attempt: ${retryCount + 1}`);
                    setRetryCount((prevCount) => prevCount + 1); // Functional form of setRetryCount
                    retryInstallPrompt(); // Retry again after interval
                }
            }, RETRY_INTERVAL);
        } else {
            console.log("Max retries reached (30). Install prompt not available.");
        }
    };

    const [browser, setBrowser] = useState<string>("");

    useEffect(() => {
        const detectedBrowser = detectBrowser();
        setBrowser(detectedBrowser);
    }, []);

    // Listen for the beforeinstallprompt event
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e); // Store the prompt event
            setInstallPromptVisible(true); // Show the install button
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

        // Start retrying if the event is not immediately available
        retryInstallPrompt();

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        };
    }, [retryCount]); // Only depend on retryCount to trigger retries

    // Handle the install button click
    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt(); // Show the install prompt
            deferredPrompt.userChoice.then((choiceResult: any) => {
                if (choiceResult.outcome === "accepted") {
                    console.log("PWA installed");
                    setIsInstalled(true);
                } else {
                    console.log("PWA installation dismissed");
                    setIsInstalled(false);
                }
                setDeferredPrompt(null); // Reset the prompt
                setInstallPromptVisible(false); // Hide the install button
            });
        }
    };

    return (
        <div className="special-page-container">
            {isInstalled ?
                <div className="content">
                    <p className="p-header">EEC App has been successfully installed on your device. You may now close the browser and launch the app or continue EEC on the browser.</p>
                    <br />
                    <button className="install-btn --button-red-color" onClick={() => { window.location.href = `${window.location.origin}${appConfig.connectionString.appBaseName}`; }}>
                        Continue EEC on the browser
                    </button>
                </div>
                :
                <div className="content">
                    <img
                        className="installation-logo"
                        src={`${appConfig.connectionString.appBaseName}/images/pwa_app_logo.PNG`}
                        style={{ height: '80px', width: '85px', borderRadius: '15px' }}
                    />
                    <br /><br />
                    <p className="p-header">Install EEC App</p>
                    {/* <p><b>You are currently using <span style={{ color: "#38748a", fontWeight: "bold" }}>{browser}.</span></b></p> */}
                    {(browser === "Microsoft Edge" || browser === "Google Chrome" || browser === "Samsung Internet" || browser === "Android WebView") && (
                        <>
                            <span>Please wait and click the button below to initiate the installation:</span><br /><br />
                            {isInstallPromptVisible ? (
                                <>
                                    <button className="install-btn --button-red-color" onClick={handleInstallClick}>
                                        Install
                                    </button>
                                    <br /><br /><br />
                                </>
                            ) : (
                                <>
                                    <span style={{ fontWeight: "bold", fontStyle: "italic", fontSize: "18px", color: "silver" }}>LOADING BUTTON...</span>
                                    <br /><br />
                                    <p><b>Note : </b>If the install button has not appeared, please follow the manual installation steps outlined below.</p><br />
                                </>
                            )}
                        </>
                    )}
                    <div style={{ textAlign: "left" }}>
                        <p><b>To manually install the EEC App from <span style={{ color: "#38748a", fontWeight: "bold" }}>{browser}:</span></b></p>
                        {(browser === "Google Chrome" || browser === "FireFox") && (
                            <>
                                <p className="p-content" style={{ color: "grey" }}></p>
                                <ol className="install-steps">
                                    <li>
                                        Press the &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/vertical-3-dots.png`}
                                            style={{ height: '22px', width: '20px', border: "1px solid lightgrey", marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        icon on the navigation toolbar.<br /><br />
                                    </li>
                                    <li>
                                        Select &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/android-add-to-home-screen.png`}
                                            style={{ height: '20px', width: '15px', marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        <b style={{ color: "black" }}>"Add to Home screen."</b><br /><br />
                                    </li>
                                    <li>
                                        Click <b style={{ color: "black" }}>"Add / Install"</b> in the popup.<br /><br />
                                    </li>
                                    <li>
                                        EEC App is now installed and available on your home screen.<br /><br />
                                    </li>
                                </ol>
                            </>

                        )}
                        {browser === "Microsoft Edge" && (
                            <>
                                <p className="p-content" style={{ color: "grey" }}></p>
                                <ol className="install-steps">
                                    <li>
                                        Press the &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/horizontal-3-lines.png`}
                                            style={{ height: '14px', width: '15px', marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        icon on the navigation toolbar.<br /><br />
                                    </li>
                                    <li>
                                        Search the menu and select &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/android-add-to-home-screen.png`}
                                            style={{ height: '20px', width: '15px', marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        <b style={{ color: "black" }}>"Add to Phone"</b><br /><br />
                                    </li>
                                    <li>
                                        Click <b style={{ color: "black" }}>"Add"</b> in the popup.<br /><br />
                                    </li>
                                    <li>
                                        The EEC App will be installed and available on your home screen.
                                    </li>
                                </ol>
                            </>

                        )}
                        {browser === "Safari" && (
                            <>
                                <p className="p-content" style={{ color: "grey" }}></p>
                                <ol className="install-steps">
                                    <li>
                                        Press the&nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/safari-share-icon.png`}
                                            style={{ height: '20px', width: '18px', marginBottom: '5px' }}
                                        />&nbsp;
                                        <b style={{ color: "black" }}>"Share"</b> icon on the navigation toolbar.<br /><br />
                                    </li>
                                    <li>
                                        Select &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/safari-add-to-home-screen.png`}
                                            style={{ height: '25px', width: '30px', marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        <b style={{ color: "black" }}>"Add to Home screen."</b><br /><br />
                                    </li>
                                    <li>
                                        EEC App is now installed and available on your home screen.<br /><br />
                                    </li>
                                </ol>
                            </>

                        )}
                        {(browser === "Samsung Internet" || browser === "Android WebView") && (
                            <>
                                <p className="p-content" style={{ color: "grey" }}> </p>
                                <ol className="install-steps">
                                    <li>
                                        Press the &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/horizontal-3-lines.png`}
                                            style={{ height: '14px', width: '15px', marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        <b style={{ color: "black" }}>"three line menu"</b> icon on the navigation toolbar.<br /><br />
                                    </li>
                                    <li>
                                        Select &nbsp;&nbsp;
                                        <img
                                            src={`${appConfig.connectionString.appBaseName}/images/safari-add-to-home-screen.png`}
                                            style={{ height: '25px', width: '30px', marginBottom: '5px' }}
                                        />&nbsp;&nbsp;
                                        <b style={{ color: "black" }}>"Add Page To"</b><br /><br />
                                    </li>
                                    <li>
                                        Select <b style={{ color: "black" }}>"App Screen"</b> in the popup.<br /><br />
                                    </li>
                                    <li>
                                        EEC App is now installed and available on your home screen.<br /><br />
                                    </li>
                                </ol>
                            </>

                        )}
                        {(browser === "Internet Explorer" || browser === "Opera" || browser === "Other") && (
                            <p className="p-content" style={{ color: "grey" }}>
                                We recommend upgrading your browser or using another PWA-compatible browser, such as <b style={{ color: "black" }}>Google Chrome, Microsoft Edge, Safari (iOS), Firefox (Mobile), Samsung Internet, or Android WebView, for an optimal experience.</b><br /><br />
                            </p>
                        )}
                    </div>
                </div>
            }
        </div>
    );
};

export default QrCodeInstall;